import { PART_SHELL, partSerial, UOM } from "../constants";
import {
  getDistinctValues,
  isEmpty,
  isTrue,
  isFalse,
  getWeightedMean,
  listFromCsv,
  toFixedSigned,
  toFixed,
  getDistinctObjectValues
} from "../utils/helpers";
import { MarkerType } from "../utils/types";

const joinChar = "#@#";

const fpoIdentifier = record => `${record?.primaryUnitId || record?.unitId || ""}${joinChar}${record?.fpo || ""}`;

export const getPartType = effPartName => {
  if (isEmpty(effPartName)) return "";
  return effPartName.split("-")[0]?.trim();
};

/**
 * Sorts size in proper serial even if some size have different serial number in different FlowInfo.
 *
 * @param {Object[]} sbList - Size Breakup List
 * @returns Sorted Size Breakup List
 */
export const getSortedSizeKeyList = sbList => {
  if (isEmpty(sbList)) return [];

  const serials = getDistinctValues(sbList.map(e => e.serialNo).filter(e => !isEmpty(e)));
  if (serials.length <= 1) {
    sbList = sbList.sort((a, b) => (a.sizeKey < b.sizeKey ? -1 : 1));
  }

  const sizeKeys = getDistinctValues(sbList.map(sb => sb.sizeKey));
  const sizeSerialListMap = sizeKeys.reduce(
    (acc, sizeKey) => ({
      ...acc,
      [sizeKey]: sbList
        .filter(sb => sb.sizeKey === sizeKey)
        .map(sb => sb.serialNo || 0)
        .sort((a, b) => b - a)
    }),
    {}
  );

  const sizeList = sizeKeys.sort((sizeA, sizeB) => {
    const aSerialList = sizeSerialListMap[sizeA];
    const bSerialList = sizeSerialListMap[sizeB];
    const n = aSerialList.length > bSerialList.length ? aSerialList.length : bSerialList.length;
    let result = 0;
    for (let i = 0; i < n; i++) {
      let aSerial = i < aSerialList.length ? aSerialList[i] : aSerialList[aSerialList.length - 1];
      let bSerial = i < bSerialList.length ? bSerialList[i] : bSerialList[bSerialList.length - 1];
      if (aSerial !== bSerial) {
        return aSerial - bSerial;
      }
    }
    return result;
  });

  return sizeList;
};

export const getEffectiveExtraPercent = flowInfoList => {
  if (isEmpty(flowInfoList)) return 0;

  const extraQty = flowInfoList.map(fi => (fi.extra || 0) * (fi.orderQty || 0) * 0.01).reduce((acc, e) => acc + e, 0);
  const totalQty = flowInfoList.reduce((acc, e) => acc + (e.orderQty || 0), 0);
  const extraPercent = totalQty > 0 ? toFixed((extraQty * 100) / totalQty, 1) : 0;
  return extraPercent;
};

export const getEffectiveFpoExtraPercent = flowInfoList => {
  if (isEmpty(flowInfoList)) return 0;

  const extraQty = flowInfoList.map(fi => (fi.fpoExtra || 0) * (fi.fpoQty || 0) * 0.01).reduce((acc, e) => acc + e, 0);
  const totalQty = flowInfoList.reduce((acc, e) => acc + (e.fpoQty || 0), 0);
  const extraPercent = totalQty > 0 ? toFixed((extraQty * 100) / totalQty, 1) : 0;
  return extraPercent;
};

export const getEffPartList = (flowInfoList, order) => {
  if (isEmpty(flowInfoList) || isEmpty(order)) return [];

  const finalFlowInfoList = getDistinctObjectValues(
    flowInfoList
      .flatMap(fi => {
        if (isEmpty(fi.orderParentId)) return [fi];
        const parent = order.flowInfoList.find(e => e.id === fi.orderParentId);
        const childList = order.flowInfoList.filter(e => e.parentId === parent?.id);
        return isEmpty(childList) ? [parent] : childList;
      })
      .filter(e => !isEmpty(e))
  );

  const productList = getDistinctObjectValues(finalFlowInfoList.map(e => e.product));

  const parentProductList = getDistinctObjectValues(
    finalFlowInfoList
      .map(fi => order?.flowInfoList?.find(e => e.id === fi.parentId))
      .filter(e => !isEmpty(e))
      .map(e => e.product)
  );
  const uomQty = productList.length > 1 && parentProductList.length > 0 && parentProductList.some(e => isTrue(e.set)) ? "Set" : "Pc";

  const partMap = finalFlowInfoList
    .flatMap(fi => fi.partList?.map(p => ({ ...p, flowInfo: fi })) || [])
    .reduce((acc, p) => ({ ...acc, [p.effPartName]: isEmpty(acc[p.effPartName]) ? [p] : [...acc[p.effPartName], p] }), {});
  const partList = Object.values(partMap).map(partList => {
    const finalPartList = partList.map(p => ({
      ...p,
      productId: p.flowInfo?.product?.id,
      ratio: p.flowInfo?.ratio || 1,
      uom: p.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : p.uom,
      ocu: p.uom === UOM.GRAM.value ? p.ocu / 1000 : p.ocu
    }));

    let bomCu, ocu;
    if (uomQty === "Set") {
      const pPartList = getDistinctObjectValues(finalPartList, "productId");
      bomCu = pPartList.reduce((acc, p) => acc + p.bomCu * p.ratio, 0);
      ocu = pPartList.reduce((acc, p) => acc + p.ocu * p.ratio, 0);
    } else {
      const bcuData = finalPartList.filter(p => !isEmpty(p.bomCu)).map(p => ({ key: p.bomCu, weight: p.flowInfo?.orderQty || 0 }));
      const ocuData = finalPartList.filter(p => !isEmpty(p.ocu)).map(p => ({ key: p.ocu, weight: p.flowInfo?.orderQty || 0 }));
      bomCu = !isEmpty(bcuData) ? getWeightedMean(bcuData) : null;
      ocu = !isEmpty(ocuData) ? getWeightedMean(ocuData) : null;
    }

    return {
      serial: partList[0]?.serial,
      partName: partList[0]?.partName,
      placement: partList[0]?.placement,
      effPartName: partList[0]?.effPartName,
      ocu,
      bomCu,
      uom: finalPartList[0]?.uom
    };
  });
  return partList;
};

/**
 * Find FlowInfo List w.r.t Warehouse where hierarchy is due to Product, Style and Color only (Not due to Inseam of Destination related fields).
 * While reforming flowInfo heirarchy, review the effective partList which is created.
 *
 * @param {Object} order - Order object with initialized FlowInfo
 * @returns Effective FlowInfo List for Warehouse related pages
 */
export const getWhFlowInfoList = order => {
  if (isEmpty(order?.flowInfoList)) return [];
  const flowInfoList = order.flowInfoList.filter(fi => isEmpty(fi.orderParentId));
  const parentList = flowInfoList.filter(fi => isEmpty(fi.parentId));
  return parentList.flatMap(parent => {
    const children = flowInfoList.filter(fi => fi.parentId === parent.id);
    if (isEmpty(children)) {
      return [
        {
          flowInfoIds: [parent.id],
          orderId: order.id,
          poRef: order.poRef,
          customerPoRef: order.customerPoRef,
          product: parent.product,
          style: parent.style,
          color: parent.color,
          styleColor: parent?.styleColor,
          partList: parent.partList,
          orderQty: parent.orderQty,
          extra: parent.extra
        }
      ];
    }

    if (isTrue(parent.isSetParent)) {
      // Ideally for Set, Each child will be different based on product, style and color
      const styleIds = getDistinctValues(children.map(c => `${c.product?.id}-${c.style}-${c.color}`));
      const childList = styleIds.map(styleId => {
        const list = children.filter(c => styleId === `${c.product?.id}-${c.style}-${c.color}`);
        const partList = getEffPartList(list, order);

        return {
          flowInfoIds: list.map(e => e.id),
          parentId: parent.id,
          orderId: order.id,
          poRef: order.poRef,
          customerPoRef: order.customerPoRef,
          product: list[0].product,
          style: list[0].style,
          color: list[0].color,
          styleColor: list[0]?.styleColor,
          partList,
          orderQty: list.reduce((acc, e) => acc + e.orderQty, 0),
          extra: getEffectiveExtraPercent(list)
        };
      });
      return [
        {
          flowInfoIds: [parent.id],
          orderId: order.id,
          poRef: order.poRef,
          customerPoRef: order.customerPoRef,
          product: parent.product,
          style: parent.style,
          color: parent.color,
          styleColor: parent?.styleColor,
          partList: parent.partList,
          orderQty: parent.orderQty,
          extra: parent.extra,
          isParent: true
        },
        ...childList
      ];
    } else {
      const isNonSet = children.every(c => isFalse(c.isSetParent));
      // Non Set Destination
      if (isNonSet) {
        const partList = getEffPartList(children, order);
        return [
          {
            flowInfoIds: [parent.id],
            orderId: order.id,
            poRef: order.poRef,
            customerPoRef: order.customerPoRef,
            product: parent.product,
            style: parent.style,
            color: parent.color,
            styleColor: parent?.styleColor,
            orderQty: parent.orderQty,
            extra: getEffectiveExtraPercent(children),
            partList
          }
        ];
      }
      // Set Destination
      else {
        const deepChildren = children.flatMap(child => {
          const childList = flowInfoList.filter(fi => fi.parentId === child.id);
          return isEmpty(childList) ? [child] : childList;
        });
        const styleIds = getDistinctValues(deepChildren.map(c => `${c.product?.id}-${c.style}-${c.color}`));
        const childList = styleIds.map(styleId => {
          const list = deepChildren.filter(c => styleId === `${c.product?.id}-${c.style}-${c.color}`);
          const partList = getEffPartList(list, order);

          return {
            flowInfoIds: list.map(e => e.id),
            parentId: parent.id,
            orderId: order.id,
            poRef: order.poRef,
            customerPoRef: order.customerPoRef,
            product: list[0].product,
            style: list[0].style,
            color: list[0].color,
            styleColor: list[0]?.styleColor,
            partList,
            orderQty: list.reduce((acc, e) => acc + e.orderQty, 0),
            extra: getEffectiveExtraPercent(list)
          };
        });
        return [
          {
            flowInfoIds: [parent.id],
            orderId: order.id,
            poRef: order.poRef,
            customerPoRef: order.customerPoRef,
            product: parent.product,
            style: parent.style,
            color: parent.color,
            styleColor: parent?.styleColor,
            partList: parent.partList,
            orderQty: parent.orderQty,
            extra: parent.extra,
            isParent: true
          },
          ...childList
        ];
      }
    }
  });
};

/**
 * Find FlowInfo List for an OrderFlow. No extra processing.
 *
 * @param {Object} order - Order object with initialized FlowInfo
 * @param {Object} flow  - Order Flow object
 * @returns FlowInfo List of current OrderFlow.
 */
// deprecated
export const getFlowInfoList = (order, flow) => {
  if (isEmpty(order?.flowInfoList) || isEmpty(flow)) return [];
  const flowInfoIds = flow?.flowInfoIds || [];
  return order?.flowInfoList?.filter(fi => flowInfoIds.includes(fi.id)) || [];
};

//* Find all flowInfo list for this cut order *//
export const getCutOrderFlowInfoList = (order, cutOrder) => {
  if (isEmpty(order?.flowInfoList) || isEmpty(cutOrder)) return [];
  const flowInfoList = order.flowInfoList
    .filter(
      fi =>
        cutOrder.productIdList?.includes(fi.product?.id) &&
        cutOrder.styleColorList?.includes(fi.styleColor) &&
        fpoIdentifier(cutOrder) === fpoIdentifier(fi)
    )
    .flatMap(fi => {
      let sbList = fi.sizeBreakupList.filter(sb => sb.type === "ORDER");
      if (isEmpty(sbList)) {
        const parent = order.flowInfoList.find(e => fi.orderParentId === e.id);
        sbList = parent?.sizeBreakupList.filter(sb => sb.type === "ORDER") || [];

        if (isEmpty(sbList)) {
          const childList = order.flowInfoList
            .filter(e => e.parentId === parent?.id)
            .filter(e => isEmpty(fi.flowRef) || e.flowRef === fi.flowRef)
            .filter(e => isEmpty(fi.delFlowRef) || e.delFlowRef === fi.delFlowRef)
            .filter(e => isEmpty(fi.customerFlowRef) || e.customerFlowRef === fi.customerFlowRef)
            .filter(e => isEmpty(fi.destination) || e.destination === fi.destination)
            .filter(e => isEmpty(fi.deliveryDate) || e.deliveryDate === fi.deliveryDate)
            .filter(e => isEmpty(fi.delMode) || e.delMode === fi.delMode);
          sbList = childList.flatMap(e => e.sizeBreakupList || []).filter(sb => sb.type === "ORDER");
        }
      }

      const sizeGroups = getDistinctValues(sbList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)));
      if (!isEmpty(fi.inseam) || isEmpty(sizeGroups) || sizeGroups?.length <= 1) {
        return [{ ...fi, orderQty: sbList.reduce((acc, sb) => acc + sb.qty, 0), sizeBreakupList: sbList }];
      } else {
        return sizeGroups.map(sizeGroup => {
          const cSbList = sbList.filter(sb => sb.sizeGroup === sizeGroup);
          const fpoSbList = fi.fpoSizeBreakupList?.filter(sb => sb.sizeGroup === sizeGroup);
          const orderQty = cSbList.reduce((acc, sb) => acc + sb.qty, 0);
          const fpoQty = fpoSbList.reduce((acc, sb) => acc + sb.qty, 0);
          return { ...fi, inseam: sizeGroup, orderQty, fpoQty, sizeBreakupList: cSbList, fpoSizeBreakupList: fpoSbList };
        });
      }
    })
    .filter(fi => isEmpty(cutOrder?.flowRefs) || listFromCsv(cutOrder?.flowRefs).includes(fi.flowRef))
    .filter(fi => isEmpty(cutOrder?.delFlowRefs) || listFromCsv(cutOrder?.delFlowRefs).includes(fi.delFlowRef))
    .filter(fi => isEmpty(cutOrder?.customerFlowRefs) || listFromCsv(cutOrder?.customerFlowRefs).includes(fi.customerFlowRef))
    .filter(fi => isEmpty(cutOrder?.inseams) || listFromCsv(cutOrder?.inseams).includes(fi.inseam))
    .filter(fi => isEmpty(cutOrder?.destinations) || listFromCsv(cutOrder?.destinations).includes(fi.destination))
    .filter(fi => isEmpty(cutOrder?.deliveries) || listFromCsv(cutOrder?.deliveries).includes(fi.deliveryDate))
    .filter(fi => isEmpty(cutOrder?.delModes) || listFromCsv(cutOrder?.delModes).includes(fi.delMode))
    .map(fi => ({ ...fi, productId: fi.product?.id }));
  return flowInfoList;
};

//* Find flowInfo list with respect to style and color only. *//
export const getEffFlowInfoList = (order, cutOrder) => {
  if (isEmpty(order?.flowInfoList) || isEmpty(cutOrder)) return [];

  const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);
  const parentProductList = getDistinctObjectValues(
    flowInfoList
      .map(fi => order?.flowInfoList?.find(e => e.id === fi.parentId))
      .filter(e => !isEmpty(e))
      .map(e => e.product)
  );
  const uomQty = cutOrder.productIdList.length > 1 && parentProductList.length > 0 && parentProductList.some(e => isTrue(e.set)) ? "Set" : "Pc";

  const styleColorList = getDistinctValues(flowInfoList.map(fi => fi.styleColor));
  const effFlowInfoList = [];
  for (let i = 0; i < styleColorList.length; i++) {
    const styleColor = styleColorList[i];
    const fiList2 = flowInfoList.filter(fi => fi.styleColor === styleColor);

    //** First Merge for Inseam **//
    const mergedInseamFlowInfoList = [];
    const ids = getDistinctValues(fiList2.map(nonInseamFlowIdentifier));
    for (let id of ids) {
      const fiList3 = fiList2.filter(fi => id === nonInseamFlowIdentifier(fi)).sort((a, b) => a.serialNo - b.serialNo);
      if (isEmpty(fiList3)) continue;
      if (fiList3.length === 1) {
        mergedInseamFlowInfoList.push(fiList3[0]);
        continue;
      }

      const mergedSizeBreakupList = [];
      const mergedFpoSizeBreakupList = [];
      for (let i = 0; i < fiList3.length; i++) {
        const flowInfo = fiList3[i];
        const sbList = flowInfo.sizeBreakupList?.filter(sb => sb.type === "ORDER").sort((a, b) => a.serialNo - b.serialNo);

        for (let sizeBreakup of sbList) {
          mergedSizeBreakupList.push({ ...sizeBreakup, serialNo: i * 100 + sizeBreakup.serialNo });
        }

        if (flowInfo.sizeBreakupList?.some(sb => sb.type === "ORDER")) {
          const fpoSbList = flowInfo.fpoSizeBreakupList?.sort((a, b) => a.serialNo - b.serialNo);
          for (let sizeBreakup of fpoSbList) {
            mergedFpoSizeBreakupList.push({ ...sizeBreakup, serialNo: i * 100 + sizeBreakup.serialNo });
          }
        }
      }

      const mergedFlowInfo = {
        ...fiList3[0],
        orderQty: mergedSizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0),
        fpoQty: mergedFpoSizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0),
        sizeBreakupList: getDistinctObjectValues([...mergedSizeBreakupList, ...mergedFpoSizeBreakupList]),
        fpoSizeBreakupList: mergedFpoSizeBreakupList,
        partList: getEffPartList(fiList2, order)
      };
      mergedInseamFlowInfoList.push(mergedFlowInfo);
    }

    let finalFlowInfoList = [...mergedInseamFlowInfoList];
    if (uomQty === "Set") {
      const productId = cutOrder.productIdList[0];
      finalFlowInfoList = mergedInseamFlowInfoList.filter(e => e.product?.id === productId);
    }

    const mergedSizeBreakupList = mergeDeliverySizeBreakup(finalFlowInfoList, "ORDER");
    const mergedFpoSizeBreakupList = mergeDeliverySizeBreakup(finalFlowInfoList, "FPO");

    const flowInfo = {
      ...finalFlowInfoList[0],
      sizeBreakupList: getDistinctObjectValues([...mergedSizeBreakupList, ...mergedFpoSizeBreakupList]),
      fpoSizeBreakupList: mergedFpoSizeBreakupList,
      partList: getEffPartList(mergedInseamFlowInfoList, order)
    };

    effFlowInfoList.push(flowInfo);
  }

  return effFlowInfoList;
};

////* Find flowInfo list with respect to style and color only. *//
//export const getEffFlowInfoList = (order, cutOrder) => {
//  if (isEmpty(order?.flowInfoList) || isEmpty(cutOrder)) return [];
//
//  const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);
//  const parentProductList = getDistinctObjectValues(
//    flowInfoList
//      .map(fi => order?.flowInfoList?.find(e => e.id === fi.parentId))
//      .filter(e => !isEmpty(e))
//      .map(e => e.product)
//  );
//  const uomQty = cutOrder.productIdList.length > 1 && parentProductList.length > 0 && parentProductList.some(e => isTrue(e.set)) ? "Set" : "Pc";
//
//  const styleColorList = getDistinctValues(flowInfoList.map(fi => fi.styleColor));
//  const effFlowInfoList = [];
//  for (let i = 0; i < styleColorList.length; i++) {
//    const styleColor = styleColorList[i];
//    const fiList2 = flowInfoList.filter(fi => fi.styleColor === styleColor);
//
//    //** First Merge for Inseam **//
//    const mergedInseamFlowInfoList = [];
//    const ids = getDistinctValues(fiList2.map(nonInseamFlowIdentifier));
//    for (let id of ids) {
//      const fiList3 = fiList2.filter(fi => id === nonInseamFlowIdentifier(fi)).sort((a, b) => a.serialNo - b.serialNo);
//      if (isEmpty(fiList3)) continue;
//      if (fiList3.length === 1) {
//        mergedInseamFlowInfoList.push(fiList3[0]);
//        continue;
//      }
//
//      const mergedSizeBreakupList = [];
//      const mergedFpoSizeBreakupList = [];
//      for (let i = 0; i < fiList3.length; i++) {
//        const flowInfo = fiList3[i];
//        const sbList = flowInfo.sizeBreakupList?.filter(sb => sb.type === "ORDER").sort((a, b) => a.serialNo - b.serialNo);
//
//        for (let sizeBreakup of sbList) {
//          mergedSizeBreakupList.push({ ...sizeBreakup, serialNo: i * 100 + sizeBreakup.serialNo });
//        }
//
//        if (flowInfo.sizeBreakupList?.some(sb => sb.type === "ORDER")) {
//          const fpoSbList = flowInfo.fpoSizeBreakupList?.sort((a, b) => a.serialNo - b.serialNo);
//          for (let sizeBreakup of fpoSbList) {
//            mergedFpoSizeBreakupList.push({ ...sizeBreakup, serialNo: i * 100 + sizeBreakup.serialNo });
//          }
//        }
//      }
//
//      const mergedFlowInfo = {
//        ...fiList3[0],
//        orderQty: mergedSizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0),
//        fpoQty: mergedFpoSizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0),
//        sizeBreakupList: getDistinctObjectValues([...mergedSizeBreakupList, ...mergedFpoSizeBreakupList]),
//        fpoSizeBreakupList: mergedFpoSizeBreakupList,
//        partList: getEffPartList(fiList2, order)
//      };
//      mergedInseamFlowInfoList.push(mergedFlowInfo);
//    }
//
//    let finalFlowInfoList = [...mergedInseamFlowInfoList];
//    if (uomQty === "Set") {
//      const productId = cutOrder.productIdList;
//      finalFlowInfoList = mergedInseamFlowInfoList.filter(e => productId?.includes(e.product?.id));
//    }
//
//    if (uomQty === "Set") {
//      finalFlowInfoList.forEach(flowInf => {
//        const mergedSizeBreakupList = mergeDeliverySizeBreakup([flowInf], "ORDER");
//        const mergedFpoSizeBreakupList = mergeDeliverySizeBreakup([flowInf], "FPO");
//
//        const flowInfo = {
//          ...flowInf,
//          sizeBreakupList: getDistinctObjectValues([...mergedSizeBreakupList, ...mergedFpoSizeBreakupList]),
//          fpoSizeBreakupList: mergedFpoSizeBreakupList,
//          partList: getEffPartList(mergedInseamFlowInfoList, order)
//        };
//        effFlowInfoList.push(flowInfo);
//      });
//    } else {
//      const mergedSizeBreakupList = mergeDeliverySizeBreakup(finalFlowInfoList, "ORDER");
//      const mergedFpoSizeBreakupList = mergeDeliverySizeBreakup(finalFlowInfoList, "FPO");
//
//      const flowInfo = {
//        ...finalFlowInfoList[0],
//        sizeBreakupList: getDistinctObjectValues([...mergedSizeBreakupList, ...mergedFpoSizeBreakupList]),
//        fpoSizeBreakupList: mergedFpoSizeBreakupList,
//        partList: getEffPartList(mergedInseamFlowInfoList, order)
//      };
//      effFlowInfoList.push(flowInfo);
//    }
//  }
//
//  return effFlowInfoList;
//};

const nonInseamFlowIdentifier = fi => {
  return [
    fi.product?.id || "P",
    fi.style,
    fi.color,
    fi.flowRef || "FR",
    fi.delFlowRef || "DFR",
    fi.customerFlowRef || "CFR",
    fi.destination || "Dest",
    fi.deliveryDate || "Del",
    fi.delMode || "DM",
    fi.primaryUnitId || "Unit"
  ].join("_");
};

const mergeDeliverySizeBreakup = (flowInfoList, sbType) => {
  if (isEmpty(flowInfoList)) return [];

  const ratio = flowInfoList[0]?.ratio || 1;
  const sizeKeySerialMap = {};

  for (let flowInfo of flowInfoList) {
    let sbList = [];
    if (sbType === "ORDER") {
      sbList = flowInfo.sizeBreakupList?.filter(sb => sb.type === "ORDER").sort((a, b) => a.serialNo - b.serialNo) || [];
    } else {
      sbList = flowInfo.fpoSizeBreakupList?.sort((a, b) => a.serialNo - b.serialNo) || [];
    }
    for (let sizeBreakup of sbList) {
      if (!isEmpty(sizeKeySerialMap[sizeBreakup.sizeKey])) {
        sizeKeySerialMap[sizeBreakup.sizeKey] = Math.min(sizeKeySerialMap[sizeBreakup.sizeKey], sizeBreakup.serialNo);
      } else {
        sizeKeySerialMap[sizeBreakup.sizeKey] = sizeBreakup.serialNo;
      }
    }
  }

  const sizeBreakupList = flowInfoList.flatMap(fi => {
    return sbType === "ORDER"
      ? fi.sizeBreakupList?.filter(sb => sb.type === "ORDER").sort((a, b) => a.serialNo - b.serialNo) || []
      : fi.fpoSizeBreakupList?.sort((a, b) => a.serialNo - b.serialNo) || [];
  });

  const finalSizeBreakupList = [];
  for (let sizeBreakup of sizeBreakupList) {
    const idx = finalSizeBreakupList.findIndex(e => e.sizeKey === sizeBreakup.sizeKey);
    let sb = finalSizeBreakupList[idx];
    if (isEmpty(sb)) {
      sb = { ...sizeBreakup, serialNo: sizeKeySerialMap[sizeBreakup.sizeKey] || sizeBreakup.serialNo, qty: sizeBreakup.qty / ratio };
      finalSizeBreakupList.push(sb);
    } else {
      finalSizeBreakupList[idx] = { ...sb, serialNo: sizeKeySerialMap[sb.sizeKey] || sb.serialNo, qty: sb.qty + sizeBreakup.qty / ratio };
    }
  }
  return finalSizeBreakupList;
};

/**
 * This return size breakup by aggregating on sizeKey and also sorting it, Size Breakup id will be lost as aggregation is happening here.
 */
export const findEffFpoSizeBreakupList = flowInfoList => {
  const inseamSerialMap = flowInfoList
    .sort((a, b) => a.serialNo - b.serialNo)
    .filter(fi => !isEmpty(fi.inseam))
    .reduce((acc, fi, idx) => ({ ...acc, [fi.inseam]: idx }), {}); // Zero indexed

  const sizeBreakupList = flowInfoList
    .sort((a, b) => a.serialNo - b.serialNo)
    .flatMap(fi => fi.fpoSizeBreakupList.map(sb => ({ ...sb, serialNo: 100 * (inseamSerialMap[fi.inseam] || 0) + sb.serialNo })) || []);

  const sortedSizeKeys = getSortedSizeKeyList(sizeBreakupList);

  const sortedSizeBreakupList = sortedSizeKeys
    .map((sizeKey, idx) => {
      const sbList = sizeBreakupList.filter(e => e.sizeKey === sizeKey);
      return {
        serialNo: idx + 1,
        sizeKey,
        type: sbList[0]?.type,
        size: sbList[0]?.size,
        sizeGroup: sbList[0]?.sizeGroup,
        qty: sbList.reduce((acc, sb) => acc + sb.qty, 0)
      };
    })
    .filter(sb => sb.qty > 0);

  return sortedSizeBreakupList;
};

/**
 * This return size breakup by aggregating on sizeKey and also sorting it, Size Breakup id will be lost as aggregation is happening here.
 */
export const findEffSizeBreakupList = (flowInfoList, order) => {
  const inseamSerialMap = flowInfoList
    .sort((a, b) => a.serialNo - b.serialNo)
    .filter(fi => !isEmpty(fi.inseam))
    .reduce((acc, fi, idx) => ({ ...acc, [fi.inseam]: idx }), {}); // Zero indexed

  const sizeBreakupList = flowInfoList
    .sort((a, b) => a.serialNo - b.serialNo)
    .flatMap(fi => {
      let sbList = fi.sizeBreakupList.filter(sb => sb.type === "ORDER");
      if (isEmpty(sbList)) {
        sbList = order.flowInfoList.filter(e => fi.orderParentId === e.id).flatMap(e => e.sizeBreakupList || []);
      }
      return sbList.map(sb => ({ ...sb, serialNo: 100 * (inseamSerialMap[fi.inseam] || 0) + sb.serialNo })) || [];
    });

  const sortedSizeKeys = getSortedSizeKeyList(sizeBreakupList);

  const sortedSizeBreakupList = sortedSizeKeys
    .map((sizeKey, idx) => {
      const sbList = sizeBreakupList.filter(e => e.sizeKey === sizeKey);
      return {
        serialNo: idx + 1,
        sizeKey,
        type: sbList[0]?.type,
        size: sbList[0]?.size,
        sizeGroup: sbList[0]?.sizeGroup,
        qty: sbList.reduce((acc, sb) => acc + sb.qty, 0)
      };
    })
    .filter(sb => sb.qty > 0);

  return sortedSizeBreakupList;
};

/**
 * This return size breakup by aggregating on sizeKey and also sorting it, Size Breakup id will be lost as aggregation is happening here.
 *
 * @param {Object} orderFlow - Order Flow object
 * @param {Object} order - Order object initialized with FlowInfo
 * @returns Sorted and aggregated SizeBreakup List
 */
export const findSizeBreakupList = (orderFlow, order) => {
  if (isEmpty(orderFlow) || isEmpty(order?.flowInfoList)) return [];
  const flowInfoList = getCutOrderFlowInfoList(order, orderFlow);
  return findEffFpoSizeBreakupList(flowInfoList);
  // const inseamSerialMap = flowInfoList
  //   .sort((a, b) => a.serialNo - b.serialNo)
  //   .filter(fi => !isEmpty(fi.inseam))
  //   .reduce((acc, fi, idx) => ({ ...acc, [fi.inseam]: idx }), {}); // Zero indexed

  // const sizeBreakupList = flowInfoList
  //   .sort((a, b) => a.serialNo - b.serialNo)
  //   .flatMap(fi => fi.fpoSizeBreakupList.map(sb => ({ ...sb, serialNo: 100 * (inseamSerialMap[fi.inseam] || 0) + sb.serialNo })) || []);

  // const sortedSizeKeys = getSortedSizeKeyList(sizeBreakupList);

  // const sortedSizeBreakupList = sortedSizeKeys
  //   .map((sizeKey, idx) => {
  //     const sbList = sizeBreakupList.filter(e => e.sizeKey === sizeKey);
  //     return {
  //       serialNo: idx + 1,
  //       sizeKey,
  //       size: sbList[0]?.size,
  //       sizeGroup: sbList[0]?.sizeGroup,
  //       qty: sbList.reduce((acc, sb) => acc + sb.qty, 0)
  //     };
  //   })
  //   .filter(sb => sb.qty > 0);

  // return sortedSizeBreakupList;
};

export const getSizeBreakupList = (cutOrderList, order) => {
  if (isEmpty(cutOrderList) || isEmpty(order?.flowInfoList)) return [];
  const flowInfoList = cutOrderList.flatMap(cutOrder => getCutOrderFlowInfoList(order, cutOrder));
  const flowInfoMap = flowInfoList.reduce((acc, fi) => ({ ...acc, [fi.id]: fi }), {});
  const finalFlowInfoList = Object.values(flowInfoMap);

  return findEffFpoSizeBreakupList(finalFlowInfoList);

  // const inseamSerialMap = finalFlowInfoList
  //   .sort((a, b) => a.serialNo - b.serialNo)
  //   .filter(fi => !isEmpty(fi.inseam))
  //   .reduce((acc, fi) => ({ ...acc, [fi.inseam]: fi.serialNo - 1 }), {}); // Zero indexed

  // const sizeBreakupList = finalFlowInfoList
  //   .sort((a, b) => a.serialNo - b.serialNo)
  //   .flatMap(fi => fi.fpoSizeBreakupList.map(sb => ({ ...sb, serialNo: 100 * (inseamSerialMap[fi.inseam] || 0) + sb.serialNo })) || []);

  // const sortedSizeKeys = getSortedSizeKeyList(sizeBreakupList);

  // const sortedSizeBreakupList = sortedSizeKeys.map((sizeKey, idx) => {
  //   const sbList = sizeBreakupList.filter(e => e.sizeKey === sizeKey);
  //   return {
  //     serialNo: idx + 1,
  //     sizeKey,
  //     size: sbList[0]?.size,
  //     sizeGroup: sbList[0]?.sizeGroup,
  //     qty: sbList.reduce((acc, sb) => acc + sb.qty, 0)
  //   };
  // });

  // return sortedSizeBreakupList;
};

export const getOrderFlowList = (order, flow) => {
  if (isEmpty(order?.orderFlowList) || isEmpty(flow)) return [];
  return order.orderFlowList.filter(
    of =>
      of.type === flow.type &&
      flow.styles === of.styles &&
      flow.colors === of.colors &&
      flow.productIdList?.every(id => of.productIdList?.includes(id))
  );
};

export const getOrderFlowListFromFlowInfo = (order, flowInfo) => {
  if (isEmpty(order?.orderFlowList) || isEmpty(flowInfo)) return [];
  return order.orderFlowList.filter(
    flow =>
      listFromCsv(flow.styles).includes(flowInfo.style) &&
      listFromCsv(flow.colors).includes(flowInfo.color) &&
      flow.productIdList?.includes(flowInfo.product?.id) &&
      flowInfo.part === flow.part
  );
};

export const getFpoList = order => {
  if (isEmpty(order?.flowInfoList)) return [];
  const flowInfoList = order.flowInfoList.filter(fi => !isEmpty(fi.primaryUnitId) && !isEmpty(fi.fpo));

  const fpoList = getDistinctValues(flowInfoList.sort((a, b) => b.fpoCreatedAt - a.fpoCreatedAt).map(fi => fi.fpo));

  return fpoList.map(fpo => {
    const fiList = flowInfoList.filter(fi => fi.fpo === fpo);
    return {
      key: fpo,
      fpo,
      externalFpo: getDistinctValues(fiList.map(e => e.externalFpo).filter(e => !isEmpty(e))).join(","),
      unitId: fiList[0]?.primaryUnitId,
      cancelled: fiList[0]?.cancelled,
      productName: getDistinctValues(fiList.map(fi => fi.product?.name)).join(", "),
      styleNo: getDistinctValues(fiList.map(fi => fi.styleNo)).join(", "),
      style: getDistinctValues(fiList.map(fi => fi.style)).join(", "),
      color: getDistinctValues(fiList.map(fi => fi.color)).join(", "),
      inseam: getDistinctValues(fiList.map(fi => fi.inseam)).join(", "),
      flowRef: getDistinctValues(fiList.map(fi => fi.flowRef)).join(", "),
      delFlowRef: getDistinctValues(fiList.map(fi => fi.delFlowRef)).join(", "),
      customerFlowRef: getDistinctValues(fiList.map(fi => fi.customerFlowRef)).join(", "),
      destination: getDistinctValues(fiList.map(fi => fi.destination)).join(", "),
      deliveryDate: getDistinctValues(fiList.map(fi => fi.deliveryDate)).join(", "),
      fpoQty: fiList.reduce((acc, fi) => acc + fi.fpoQty, 0),
      extra: fiList.length === 1 ? fiList[0].extra : null,
      fpoExtra: fiList.length === 1 ? fiList[0].fpoExtra : null,
      states: getDistinctValues(fiList.flatMap(fi => (isEmpty(fi.state) ? [] : JSON.parse(fi.state))).map(e => e.state)).join(", "),
      flowInfoList: fiList
    };
  });
};

export const getFpoLabel = (fpo, fpoList, fields = []) => {
  if (isEmpty(fpo) || isEmpty(fpoList)) return "";

  const products = getDistinctValues(fpoList.map(fpo => fpo.products));
  const styles = getDistinctValues(fpoList.map(fpo => fpo.styles));
  const colors = getDistinctValues(fpoList.map(fpo => fpo.colors));
  const inseams = getDistinctValues(fpoList.map(fpo => fpo.inseams));
  const destinations = getDistinctValues(fpoList.map(fpo => fpo.destinations));
  const deliveryDates = getDistinctValues(fpoList.map(fpo => fpo.deliveryDates));
  const delModes = getDistinctValues(fpoList.map(fpo => fpo.delModes));
  const flowRefs = getDistinctValues(fpoList.map(fpo => fpo.flowRefs));
  const delFlowRefs = getDistinctValues(fpoList.map(fpo => fpo.delFlowRefs));
  const customerFlowRefs = getDistinctValues(fpoList.map(fpo => fpo.customerFlowRefs));

  const finalFields = Array.isArray(fields) ? fields : [];

  let labelItems = [];
  labelItems.push(fpo.fpo);
  labelItems.push(fpo.unitId);

  if ((finalFields.includes("product") || products?.length > 1) && !isEmpty(fpo.products)) {
    labelItems.push(fpo.products);
  }
  if ((finalFields.includes("style") || styles?.length > 1) && !isEmpty(fpo.styles)) {
    labelItems.push(fpo.styles);
  }
  if ((finalFields.includes("color") || colors?.length > 1) && !isEmpty(fpo.colors)) {
    labelItems.push(fpo.colors);
  }
  if ((finalFields.includes("inseam") || inseams?.length > 1) && !isEmpty(fpo.inseams)) {
    labelItems.push(fpo.inseams);
  }
  if ((finalFields.includes("destination") || destinations?.length > 1) && !isEmpty(fpo.destinations)) {
    labelItems.push(fpo.destinations);
  }
  if ((finalFields.includes("deliveryDate") || deliveryDates?.length > 1) && !isEmpty(fpo.deliveryDates)) {
    labelItems.push(fpo.deliveryDates);
  }
  if ((finalFields.includes("delMode") || delModes?.length > 1) && !isEmpty(fpo.delModes)) {
    labelItems.push(fpo.delModes);
  }
  if ((finalFields.includes("flowRef") || flowRefs?.length > 1) && !isEmpty(fpo.flowRefs)) {
    labelItems.push(fpo.flowRefs);
  }
  if ((finalFields.includes("delFlowRef") || delFlowRefs?.length > 1) && !isEmpty(fpo.delFlowRefs)) {
    labelItems.push(fpo.delFlowRefs);
  }
  if ((finalFields.includes("customerFlowRef") || customerFlowRefs?.length > 1) && !isEmpty(fpo.customerFlowRefs)) {
    labelItems.push(fpo.customerFlowRefs);
  }

  return labelItems.join(" -> ");
};

export const getAllocOrderLabel = (groupOrder, order, fields = []) => {
  if (isEmpty(groupOrder) || isEmpty(order?.flowInfoList)) return "";
  const productIds = getDistinctValues(order.flowInfoList.map(fi => fi.product?.id).filter(e => !isEmpty(e)));
  const styleList = getDistinctValues(order.flowInfoList.map(fi => fi.style).filter(e => !isEmpty(e)));
  const colorList = getDistinctValues(order.flowInfoList.map(fi => fi.color).filter(e => !isEmpty(e)));
  const finalFields = Array.isArray(fields) ? fields : [];

  let labelItems = [];
  if (finalFields.includes("poRef")) {
    labelItems.push(order.poRef);
  }
  if ((finalFields.includes("product") || productIds?.length > 1) && !isEmpty(groupOrder.product?.name)) {
    labelItems.push(groupOrder.product?.name);
  }
  if ((finalFields.includes("style") || styleList?.length > 1) && !isEmpty(groupOrder.styles)) {
    labelItems.push(groupOrder.styleList?.join(", "));
  }
  if ((finalFields.includes("color") || colorList?.length > 1) && !isEmpty(groupOrder.colors)) {
    labelItems.push(groupOrder.colorList?.join(", "));
  }
  return labelItems.join(" -> ");
};

export const getGroupOrderLabel = (groupOrder, order, fields = []) => {
  if (isEmpty(groupOrder) || isEmpty(order?.flowInfoList)) return "";
  const fpoList = getDistinctValues(order.flowInfoList.map(fi => fi.fpo).filter(e => !isEmpty(e)));
  const unitIds = getDistinctValues(order.flowInfoList.map(fi => fi.primaryUnitId).filter(e => !isEmpty(e)));
  const productIds = getDistinctValues(order.flowInfoList.map(fi => fi.product?.id).filter(e => !isEmpty(e)));
  const styleList = getDistinctValues(order.flowInfoList.map(fi => fi.style).filter(e => !isEmpty(e)));
  const colorList = getDistinctValues(order.flowInfoList.map(fi => fi.color).filter(e => !isEmpty(e)));
  const finalFields = Array.isArray(fields) ? fields : [];

  let labelItems = [];
  if (finalFields.includes("poRef")) {
    labelItems.push(order.poRef);
  }
  if ((finalFields.includes("fpo") || fpoList?.length > 1) && !isEmpty(groupOrder.fpo)) {
    labelItems.push(groupOrder.fpo);
  }
  if ((finalFields.includes("unit") || unitIds?.length > 1) && !isEmpty(groupOrder.unitId)) {
    labelItems.push(groupOrder.unitId);
  }
  if ((finalFields.includes("product") || productIds?.length > 1) && !isEmpty(groupOrder.product?.name)) {
    labelItems.push(groupOrder.product?.name);
  }
  if ((finalFields.includes("style") || styleList?.length > 1) && !isEmpty(groupOrder.styles)) {
    labelItems.push(groupOrder.styleList?.join(", "));
  }
  if ((finalFields.includes("color") || colorList?.length > 1) && !isEmpty(groupOrder.colors)) {
    labelItems.push(groupOrder.colorList?.join(", "));
  }
  labelItems.push(groupOrder.lotValue);
  return labelItems.join(" -> ");
};

export const getCutOrderLabel = (curOrder, order, fields = []) => {
  if (isEmpty(curOrder) || isEmpty(order?.flowInfoList)) return "";
  const fpoList = getDistinctValues(order.flowInfoList.map(fi => fi.fpo).filter(e => !isEmpty(e)));
  const unitIds = getDistinctValues(order.flowInfoList.map(fi => fi.primaryUnitId).filter(e => !isEmpty(e)));
  const productIds = getDistinctValues(order.flowInfoList.map(fi => fi.product?.id).filter(e => !isEmpty(e)));
  const styleList = getDistinctValues(order.flowInfoList.map(fi => fi.style).filter(e => !isEmpty(e)));
  const colorList = getDistinctValues(order.flowInfoList.map(fi => fi.color).filter(e => !isEmpty(e)));
  const inseamList = getDistinctValues(order.flowInfoList.map(fi => fi.inseam).filter(e => !isEmpty(e)));
  const destinationList = getDistinctValues(order.flowInfoList.map(fi => fi.destination).filter(e => !isEmpty(e)));
  const deliveryDateList = getDistinctValues(order.flowInfoList.map(fi => fi.deliveryDate).filter(e => !isEmpty(e)));
  const delModeList = getDistinctValues(order.flowInfoList.map(fi => fi.delMode).filter(e => !isEmpty(e)));
  const flowRefList = getDistinctValues(order.flowInfoList.map(fi => fi.flowRef).filter(e => !isEmpty(e)));
  const delFlowRefList = getDistinctValues(order.flowInfoList.map(fi => fi.delFlowRef).filter(e => !isEmpty(e)));
  const customerFlowRefList = getDistinctValues(order.flowInfoList.map(fi => fi.customerFlowRef).filter(e => !isEmpty(e)));

  const finalFields = Array.isArray(fields) ? fields : [];

  let labelItems = [];
  if (finalFields.includes("poRef")) {
    labelItems.push(order.poRef);
  }
  if ((finalFields.includes("fpo") || fpoList?.length > 1) && !isEmpty(curOrder.fpo)) {
    labelItems.push(curOrder.fpo);
  }
  if ((finalFields.includes("unit") || unitIds?.length > 1) && !isEmpty(curOrder.unitId)) {
    labelItems.push(curOrder.unitId);
  }
  if ((finalFields.includes("product") || productIds?.length > 1) && !isEmpty(curOrder.product?.name)) {
    labelItems.push(curOrder.product?.name);
  }
  if ((finalFields.includes("style") || styleList?.length > 1) && !isEmpty(curOrder.styles)) {
    labelItems.push(curOrder.styleList?.join(", "));
  }
  if ((finalFields.includes("color") || colorList?.length > 1) && !isEmpty(curOrder.colors)) {
    labelItems.push(curOrder.colorList?.join(", "));
  }
  if (finalFields.includes("lot")) {
    labelItems.push(curOrder.lotValue);
  }
  if ((finalFields.includes("inseam") || inseamList?.length > 1) && !isEmpty(curOrder.inseams)) {
    labelItems.push(curOrder.inseams);
  }
  if ((finalFields.includes("destination") || destinationList?.length > 1) && !isEmpty(curOrder.destinations)) {
    labelItems.push(curOrder.destinations);
  }
  if ((finalFields.includes("deliveryDate") || deliveryDateList?.length > 1) && !isEmpty(curOrder.deliveries)) {
    labelItems.push(curOrder.deliveries);
  }
  if ((finalFields.includes("delMode") || delModeList?.length > 1) && !isEmpty(curOrder.delModes)) {
    labelItems.push(curOrder.delModes);
  }
  if ((finalFields.includes("flowRef") || flowRefList?.length > 1) && !isEmpty(curOrder.flowRefs)) {
    labelItems.push(curOrder.flowRefs);
  }
  if ((finalFields.includes("delFlowRef") || delFlowRefList?.length > 1) && !isEmpty(curOrder.delFlowRefs)) {
    labelItems.push(curOrder.delFlowRefs);
  }
  if ((finalFields.includes("customerFlowRef") || customerFlowRefList?.length > 1) && !isEmpty(curOrder.customerFlowRefs)) {
    labelItems.push(curOrder.customerFlowRefs);
  }
  return labelItems.join(" -> ");
};

export const getSizeRatio = (markerName, sizeKeys, getSizeKey = e => e) => {
  if (!markerName) return {};
  let sizeRatio = {};
  const splits = markerName.split(",");
  for (let i = 0; i < splits.length; i++) {
    const t = splits[i].trim().split(":");
    sizeRatio[t[0]] = parseInt(t[1]);
  }

  if (!isEmpty(sizeKeys)) {
    return sizeKeys.reduce((acc, sKey) => ({ ...acc, [getSizeKey(sKey)]: sizeRatio[sKey] || "-" }), {});
  }
  return sizeRatio;
};

export const getShrinkage = ({ warpShrinkage, weftShrinkage }) => {
  return warpShrinkage && weftShrinkage ? `L${toFixedSigned(warpShrinkage, 1)}% W${toFixedSigned(weftShrinkage, 1)}%` : "";
};

export const parseMarkerName = markerName => {
  if (isEmpty(markerName)) return { sizeGroup: "", name: "" };
  if (!markerName.includes("_")) return { sizeGroup: "", name: markerName };
  const sizeGroup = getDistinctValues(markerName.split(", ").map(e => e.split("_")[0]));
  const name = markerName
    ?.split(", ")
    .map(e => e.split("_")[1])
    .join(", ");

  return { sizeGroup: sizeGroup[0], name };
};

/// Callbacks for Sorting ///
export const cbSortFlowInfo = (a, b, partSerial) => {
  const serialMap = isEmpty(partSerial) ? {} : partSerial;
  if (a.style === b.style) {
    if (a.color === b.color) {
      if (a.productId === b.productId) {
        if (!isEmpty(serialMap[a.part]) && !isEmpty(serialMap[b.part])) {
          return serialMap[a.part] - serialMap[b.part];
        } else {
          if (a.part !== PART_SHELL && b.part !== PART_SHELL) {
            return a.part < b.part ? -1 : 1;
          } else {
            return a.part === PART_SHELL ? -1 : 1;
          }
        }
      } else {
        // return a.serialNo === b.serialNo ? a.productId - b.productId : a.serialNo - b.serialNo;
        return a.productId - b.productId;
      }
    } else {
      // return a.serialNo === b.serialNo ? (a.color < b.color ? -1 : 1) : a.serialNo - b.serialNo;
      return a.color < b.color ? -1 : 1;
    }
  } else {
    // return a.serialNo === b.serialNo ? (a.style < b.style ? -1 : 1) : a.serialNo - b.serialNo;
    return a.style < b.style ? -1 : 1;
  }
};

const parseEffPartName = effPartName => {
  if (isEmpty(effPartName) || !effPartName.includes(" - ")) return { part: effPartName, placement: undefined };
  const idx = effPartName.indexOf(" - ");
  const part = effPartName.substring(0, idx);
  const placement = effPartName.substring(idx + 1);
  return { part, placement };
};

export const cbSortFlow = (a, b, partSerial) => {
  const serialMap = isEmpty(partSerial) ? {} : partSerial;

  if (a.styleColors === b.styleColors) {
    if (a.productId === b.productId) {
      if (a.unitId === b.unitId) {
        if (a.lot === b.lot) {
          const aPart = parseEffPartName(a.part)?.part;
          const bPart = parseEffPartName(b.part)?.part;
          if (!isEmpty(serialMap[aPart]) && !isEmpty(serialMap[bPart])) {
            return serialMap[aPart] - serialMap[bPart];
          } else {
            if (aPart !== PART_SHELL && bPart !== PART_SHELL) {
              return a.part < b.part ? -1 : 1;
            } else {
              return aPart === PART_SHELL ? -1 : 1;
            }
          }
        } else {
          return a.lot - b.lot;
        }
      } else {
        return a.unitId < b.unitId ? -1 : 1;
      }
    } else {
      if (isEmpty(a.product) || isEmpty(b.product)) {
        if (isEmpty(a.productName) || isEmpty(b.productName)) {
          return a.productId - b.productId;
        } else {
          return a.productName < b.productName ? -1 : 1;
        }
      } else {
        return a.product?.name < b.product?.name ? -1 : 1;
      }
    }
  } else {
    return a.styleColors < b.styleColors ? -1 : 1;
  }
};

export const cbMarkerSort = (a, b) => {
  if (a.markerNo && b.markerNo) {
    return a.markerNo === b.markerNo ? b.qtyPercent - a.qtyPercent : a.markerNo - b.markerNo;
  } else {
    // For Older data
    if (a.markerType === b.markerType) {
      if ((a.layLength > 0.0 && b.layLength > 0.0) || (a.layLength === 0.0 && b.layLength === 0.0)) {
        if (a.noOfWays === b.noOfWays) {
          if (a.qtyPercent === b.qtyPercent) {
            return b.length - a.length;
          } else {
            return b.qtyPercent - a.qtyPercent;
          }
        } else {
          return b.noOfWays - a.noOfWays;
        }
      } else {
        return a.layLength === 0.0 ? 1 : -1;
      }
    } else if (a.markerType === MarkerType.FINAL) {
      return -1;
    } else {
      return 1;
    }
  }
};

export const cbLayRowSort = (a, b) => {
  if (isEmpty(a.serialNo) && isEmpty(b.serialNo)) {
    if (a.color < b.color) return -1;
    else if (a.color > b.color) return 1;
    else {
      if (a.shade < b.shade) return -1;
      else if (a.shade > b.shade) return 1;
      else return (a.next && b.next) || (!a.next && !b.next) ? a.plannedBalance - b.plannedBalance : a.next ? 1 : -1;
    }
  } else {
    return a.serialNo - b.serialNo;
  }
};

/**
 * @deprecated use partSerial value from setting
 */
export const cbPartSort = (a, b) => {
  if (a.color < b.color) return -1;
  else if (a.color > b.color) return 1;
  else {
    if (a.part.includes(PART_SHELL) && b.part.includes(PART_SHELL)) {
      return a.part < b.part ? -1 : 1;
    } else {
      return partSerial[a.part] - partSerial[b.part];
    }
  }
};

/**
 * @deprecated use partSerial value from setting
 */
export const cbOrderConfigSort = (a, b) => {
  if (a.part === b.part) {
    if (a.colors === b.colors) {
      return a.lot - b.lot;
    } else {
      return a.colors < b.colors ? -1 : 1;
    }
  } else {
    if (a.part.includes(PART_SHELL) && b.part.includes(PART_SHELL)) {
      return a.part < b.part ? -1 : 1;
    } else {
      return partSerial[a.part] - partSerial[b.part];
    }
  }
};
